import { type FC } from 'react';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import type { SpecialEvent } from '@/types';

interface EventInfoSectionProps {
  event: SpecialEvent;
}

/**
 * Event information section displaying key details
 * Shows description, date/time, and venue/location
 */
export const EventInfoSection: FC<EventInfoSectionProps> = ({ event }) => {
  // Format date nicely
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format time
  const formatTime = (timeStr: string | null): string => {
    if (!timeStr) return 'TBA';
    try {
      const parts = timeStr.split(':');
      const hours = parts[0] || '0';
      const minutes = parts[1] || '00';
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  return (
    <section className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 py-16 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-turquoise-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Description */}
          <div className="mb-12">
            <h2 className="text-4xl font-viking text-white mb-6 drop-shadow-lg">About the Event</h2>
            <div className="prose prose-lg max-w-none">
              {event.description.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-white/90 leading-relaxed mb-4 text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date & Time */}
            <Card className="bg-turquoise-900/50 backdrop-blur-sm border-2 border-turquoise-500/50 shadow-xl hover:border-orange-500/50 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                  <Calendar className="h-6 w-6 text-orange-400" />
                </div>
                <CardTitle className="text-xl font-viking text-orange-300">Date & Time</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base font-medium text-white">
                  {formatDate(event.date)}
                </p>
                {event.startTime && (
                  <p className="text-sm text-orange-200 mt-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {formatTime(event.startTime)}
                    {event.endTime && ` - ${formatTime(event.endTime)}`}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Venue & Location */}
            <Card className="bg-turquoise-900/50 backdrop-blur-sm border-2 border-turquoise-500/50 shadow-xl hover:border-orange-500/50 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                  <MapPin className="h-6 w-6 text-orange-400" />
                </div>
                <CardTitle className="text-xl font-viking text-orange-300">Venue & Location</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base font-medium text-white">{event.venue}</p>
                <p className="text-sm text-orange-200 mt-1">{event.location}</p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </section>
  );
};
